import React, { useImperativeHandle, useRef, useEffect, forwardRef, useState } from "react";
import { Modal } from 'bootstrap';
import Loader from "../../../components/ui/Loader/Loader";
import HttpAPICall from "../../../services/HttpAPICall";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { BACKOFFICE_BASE_URL } from "../../../services/TapApiUrls";
import TapSelect from "../../../components/ui/TapSelect";

const ChildDeviceTypeLinking = forwardRef(({ detailData, onLinkSuccess }, ref) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [selectedChildType, setSelectedChildType] = useState(null);
    const [formData, setFormData] = useState({
        parent_device_type_id: "",
        child_device_type_id: "",
    });
    const access_token = useSelector(state => state.auth.access_token);
    const modalElem = useRef(null);
    const searchModal = useRef(null);

    useImperativeHandle(ref, () => ({
        initChildDeviceTypeModalHandler: () => {
            if (detailData?.id) {                
                setFormData(prev => ({
                    ...prev,
                    parent_device_type_id: detailData?.id
                }));
                fetchDeviceTypes();
                searchModal.current.show();
            } else {
                toast.error('Device type not found for linking');
            }
        }
    }));

    useEffect(() => {
        modalElem.current = document.getElementById("childDeviceTypeLinkingModal");
        searchModal.current = new Modal(modalElem.current, { keyboard: false, backdrop: false });
        return () => {
            if (searchModal.current) {
                searchModal.current.dispose();
            }
        }
    }, []);

    const fetchDeviceTypes = () => {
        setLoading(true);
        HttpAPICall.withAthorization('GET', `${BACKOFFICE_BASE_URL}/child_link_form_data`, access_token, {}, {},
            (response) => {
                const formattedTypes = response?.data?.child_devices?.map(type => ({
                    label: type.device_id,
                    value: type.id.toString()
                }));
                setDeviceTypes(formattedTypes);
            })
            .catch((error) => {
                toast.error('Failed to load device types');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const searchModalClose = () => {
        searchModal.current && searchModal.current.hide();
        resetForm();
    };

    const resetForm = () => {
        setSelectedChildType(null);
        setFormData({
            parent_device_type_id: "",
            child_device_type_id: ""
        });
    };

    const handleChildTypeChange = (selectedOption) => {
        setSelectedChildType(selectedOption);
        setFormData(prev => ({
            ...prev,
            child_device_type_id: selectedOption?.value || ""
        }));
    };

    const submitData = (e) => {
        e.preventDefault();
        if (!formData.child_device_type_id) {
            toast.error('Please select a child device type');
            return;
        }
        if (!formData.parent_device_type_id) {
            toast.error('Parent device type not found');
            return;
        }
        const payload = {
            parent_device_id: parseInt(formData.parent_device_type_id),
            child_device_id: parseInt(formData.child_device_type_id),
            device_id :detailData.id
        };
        setSubmitting(true);
        HttpAPICall.withAthorization('PUT', `${BACKOFFICE_BASE_URL}/device_child_link`, access_token, {}, payload,
            (response) => {
                toast.success(response.data.msg, { position: toast.POSITION.TOP_RIGHT });
                searchModalClose();
                if (onLinkSuccess && detailData?.id) {
                    onLinkSuccess(detailData.id);
                }
            })
            .catch((error) => {
                toast.error(error.msg, { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <>
            <div className="modal fade" id="childDeviceTypeLinkingModal" tabIndex="-1" ref={modalElem}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="childDeviceTypeLinkingModalLabel">
                                Link Child Device Type
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={searchModalClose}
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {loading ? (
                                <div className="text-center py-4">
                                    <Loader />
                                </div>
                            ) : (
                                <form id="childDeviceTypeLinkingForm" onSubmit={submitData}>
                                    <div className="row mt-3 align-items-center">
                                        <label className="col-sm-3 form-label">Device ID:</label>
                                        <div className="col-sm-8">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={detailData?.device_id || ''}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="row mt-3 align-items-center">
                                        <label className="col-sm-3 form-label">Parent Device Type:</label>
                                        <div className="col-sm-8">
                                            <input type="text"
                                                className="form-control"
                                                value={detailData?.device_type?.name || ''}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="row mt-3 align-items-center">
                                        <label className="col-sm-3 form-label require">
                                            Child Device Type
                                        </label>
                                        <div className="col-sm-8">
                                            <TapSelect
                                                options={deviceTypes}
                                                changeEvent={handleChildTypeChange}
                                                isSearchable={true}
                                                isClearable={true}
                                                value={selectedChildType}
                                                placeholder="Select Child Device Type"
                                                containerHeight="38px"
                                                fontSize="14px"
                                                isRequired={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top-0 pt-0 mt-3">
                                        <button type="button" className="btn btn-secondary" onClick={searchModalClose}
                                            disabled={submitting}> Close</button>
                                        <button type="submit" className="btn btn-primary" disabled={!selectedChildType || submitting}
                                        > Submit
                                            {submitting && (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default ChildDeviceTypeLinking;